/**
 * Authentication API Service
 */

import api from "../client";
import { ENDPOINTS } from "../endpoints";

export interface LoginRequest {
  email: string;
  password: string;
  otp?: string;
}

// LoginResponse no longer carries tokens — they ride as HttpOnly cookies
// (si_at, si_rt) that the browser attaches automatically. expires_in is
// kept so the SPA can preemptively call /refresh before the access cookie
// expires.
export interface LoginUserInfo {
  email: string;
}

export interface LoginResponse {
  token_type: string;
  expires_in: number;
  user: LoginUserInfo;
}

// Refresh + logout request bodies are empty — refresh token is read from
// the si_rt cookie on the backend.

export interface RefreshResponse {
  expires_in: number;
}

export interface LogoutResponse {
  success: boolean;
}

export interface UserInfo {
  keycloak_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  roles: string[];
  /**
   * True when the user has a local Keycloak password credential.
   * False for users who authenticate only through an upstream SSO IdP.
   * Defaults to true so the Change Password section stays visible if
   * the backend did not include the field (e.g. older cache).
   */
  has_local_password: boolean;
}

/**
 * How an organization authenticates users. Drives whether the SPA renders
 * password / MFA settings, the SSO buttons on the login page, etc.
 *
 * Mirrors backend enum ``app.models.organization.OrgAuthMode``.
 */
export type AuthMode = "password_only" | "sso_only" | "both";

export interface OrganizationInfo {
  id: string;
  name: string;
  slug: string;
  auth_mode: AuthMode;
}

export interface MeResponse {
  user: UserInfo;
  organization: OrganizationInfo;
}

// ──────────────────────────────────────────────────────────────────────
// SSO flow
// ──────────────────────────────────────────────────────────────────────

export interface SSOIdPDescriptor {
  alias: string;
  display_name: string;
  provider_type: "oidc" | "saml";
  provider_name: string;
}

export interface OrgConfigResponse {
  org_slug: string;
  org_name: string;
  auth_mode: AuthMode;
  sso_enabled: boolean;
  idps: SSOIdPDescriptor[];
}

export interface SSOInitResponse {
  sso_url: string;
  state: string;
}

export interface SSOCallbackRequest {
  code: string;
  state: string;
  org_slug: string;
}

export interface SSOCallbackResponse {
  expires_in: number;
  user: LoginUserInfo;
}

export interface MFAStatusResponse {
  enabled: boolean;
}

export interface MFASetupResponse {
  enabled: boolean;
  message: string;
}

export interface MFADisableResponse {
  success: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  success: boolean;
}

export interface UpdateProfileRequest {
  first_name: string;
  last_name: string;
}

export interface UpdateProfileResponse {
  success: boolean;
  first_name: string;
  last_name: string;
}

export const authApi = {
  /**
   * Check if MFA is enabled for a given email (public)
   */
  checkMfaStatus: async (email: string): Promise<MFAStatusResponse> => {
    return api.post<MFAStatusResponse>(ENDPOINTS.AUTH_MFA_CHECK, { email });
  },

  /**
   * Get MFA status for current user
   */
  getMfaStatus: async (): Promise<MFAStatusResponse> => {
    return api.get<MFAStatusResponse>(ENDPOINTS.AUTH_MFA_STATUS);
  },

  /**
   * Setup MFA
   */
  setupMfa: async (): Promise<MFASetupResponse> => {
    return api.post<MFASetupResponse>(ENDPOINTS.AUTH_MFA_SETUP, {});
  },

  /**
   * Disable MFA
   */
  disableMfa: async (password: string, otp: string): Promise<MFADisableResponse> => {
    return api.post<MFADisableResponse>(ENDPOINTS.AUTH_MFA_DISABLE, { password, otp });
  },

  /**
   * Request password reset email
   */
  forgotPassword: async (email: string): Promise<ForgotPasswordResponse> => {
    return api.post<ForgotPasswordResponse>(ENDPOINTS.AUTH_FORGOT_PASSWORD, {
      email,
    });
  },

  /**
   * Change password for authenticated user
   */
  changePassword: async (data: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
    return api.post<ChangePasswordResponse>(ENDPOINTS.AUTH_CHANGE_PASSWORD, data);
  },

  /**
   * Login with email and password
   */
  login: async (email: string, password: string, otp?: string): Promise<LoginResponse> => {
    return api.post<LoginResponse>(ENDPOINTS.AUTH_LOGIN, { email, password, otp });
  },

  /**
   * Rotate the session cookies. The refresh token is read from the si_rt
   * HttpOnly cookie by the backend — no body needed.
   */
  refreshToken: async (): Promise<RefreshResponse> => {
    return api.post<RefreshResponse>(ENDPOINTS.AUTH_REFRESH, {});
  },

  /**
   * Logout — invalidate the Keycloak session and clear si_at + si_rt.
   * The refresh token is read from the si_rt HttpOnly cookie.
   */
  logout: async (): Promise<LogoutResponse> => {
    return api.post<LogoutResponse>(ENDPOINTS.AUTH_LOGOUT, {});
  },

  /**
   * Fetch SSO config for an org. Public — used by the login page to
   * decide which UI to render (password form, SSO button, or both).
   */
  getOrgConfig: async (org_slug: string): Promise<OrgConfigResponse> => {
    return api.get<OrgConfigResponse>(
      `${ENDPOINTS.AUTH_ORG_CONFIG}?org_slug=${encodeURIComponent(org_slug)}`,
    );
  },

  /**
   * Begin an SSO login attempt. Backend generates PKCE+state, sets the
   * si_sso_state HttpOnly cookie (via Set-Cookie on the XHR response),
   * and returns the Keycloak authorization URL to navigate to.
   *
   * Caller must do ``window.location.href = response.sso_url`` after
   * this resolves — never navigate directly to the init endpoint, as
   * that would hit the Vite dev server (not the backend) and loop back
   * to /login.
   */
  initSso: async (org_slug: string, idp_alias: string): Promise<SSOInitResponse> => {
    return api.get<SSOInitResponse>(
      `${ENDPOINTS.AUTH_SSO_INIT}?org_slug=${encodeURIComponent(org_slug)}&idp_alias=${encodeURIComponent(idp_alias)}`,
    );
  },

  /**
   * Complete the SSO redirect chain. Browser carries the si_sso_state
   * cookie set at /sso/init; backend verifies + exchanges the KC code
   * + sets the session cookies.
   */
  ssoCallback: async (
    payload: SSOCallbackRequest,
  ): Promise<SSOCallbackResponse> => {
    return api.post<SSOCallbackResponse>(ENDPOINTS.AUTH_SSO_CALLBACK, payload);
  },

  /**
   * Get current authenticated user and organization info
   */
  getMe: async (): Promise<MeResponse> => {
    return api.get<MeResponse>(ENDPOINTS.AUTH_ME);
  },

  /**
   * Update first and last name for the authenticated user
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<UpdateProfileResponse> => {
    return api.patch<UpdateProfileResponse>(ENDPOINTS.AUTH_UPDATE_PROFILE, data);
  },
};

export default authApi;
